import inventoryText from "../inventoryI18n.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { NavLink } from "react-router-dom";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarExport,
} from "@mui/x-data-grid";

import { API_URL } from "../../components/api";
import ExcelSVG from "../ExcelSVG.svg";
import FilterSVG from "../FilterSVG.svg";
import "../Inventory.css";
import "../InventoryTable.css";
import "../Purchases/Purchases.css";
import "../Suppliers/Suppliers.css";
import "./Evaluations.css";

const MAX_QUANTITY = 2147483647;

const requestConfig = () => {
  let storedTokens = {};

  try {
    storedTokens = JSON.parse(
      localStorage.getItem("authTokens") || "{}"
    );
  } catch {
    storedTokens = {};
  }

  return {
    headers: {
      Authorization: `Bearer ${storedTokens.access || ""}`,
    },
  };
};

const responseMessage = (error, fallback) => {
  const message = error?.response?.data?.message;

  return typeof message === "string" && message.trim()
    ? message
    : fallback;
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
};

const actualNumber = (value) => {
  const cleaned = String(value ?? "").trim();

  if (!/^[0-9]+$/.test(cleaned)) {
    return null;
  }

  const number = Number(cleaned);

  if (
    !Number.isSafeInteger(number) ||
    number < 0 ||
    number > MAX_QUANTITY
  ) {
    return null;
  }

  return number;
};

const EVALUATION_FILTER_FIELDS = [
  {
    field: "warehouse_name",
    label: "Warehouse",
  },
  {
    field: "stock_operator_name",
    label: "Stock Operator",
  },
  {
    field: "creation_date",
    label: "Evaluation Date",
  },
  {
    field: "status_label",
    label: "Status",
  },
];

const EVALUATION_EXPORT_COLUMNS = [
  {
    field: "warehouse_name",
    header: "Warehouse",
  },
  {
    field: "stock_operator_name",
    header: "Stock Operator",
  },
  {
    field: "creation_date",
    header: "Evaluation Date",
  },
  {
    field: "status_label",
    header: "Status",
  },
];

function InventoryEvaluations() {
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [searchQuery, setStoredSearchQuery] = useState(
    () => sessionStorage.getItem("searchEvaluation") || ""
  );
  const setSearchQuery = (value) => {
    sessionStorage.setItem("searchEvaluation", value);
    setStoredSearchQuery(value);
  };
  const [notice, setNotice] = useState(null);
  const [filters, setFilters] = useState({
    warehouse_name: "",
    stock_operator_name: "",
    creation_date: "",
    status_label: "",
  });
  const [showFilterModal, setShowFilterModal] =
    useState(false);
  const [
    columnVisibilityModel,
    setColumnVisibilityModel,
  ] = useState({});

  const [showAdd, setShowAdd] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [operators, setOperators] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [operatorId, setOperatorId] = useState("");
  const [products, setProducts] = useState([]);
  const [unlistedProductOptions, setUnlistedProductOptions] =
    useState([]);
  const [selectedUnlistedProductId, setSelectedUnlistedProductId] =
    useState("");
  const [productsLoading, setProductsLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [showReport, setShowReport] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  const fetchEvaluations = useCallback(async () => {
    setLoading(true);
    setPageError("");

    try {
      const response = await axios.get(
        `${API_URL}/inventory/evaluations/`,
        requestConfig()
      );

      setEvaluations(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (error) {
      setEvaluations([]);
      setPageError(
        responseMessage(
          error,
          "Inventory evaluations could not be loaded."
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvaluations();
  }, [fetchEvaluations]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setNotice(null);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [notice]);

  const resetAddModal = useCallback(() => {
    setShowAdd(false);
    setWarehouses([]);
    setOperators([]);
    setWarehouseId("");
    setOperatorId("");
    setProducts([]);
    setUnlistedProductOptions([]);
    setSelectedUnlistedProductId("");
    setOptionsLoading(false);
    setProductsLoading(false);
    setFormError("");
  }, []);

  const closeAddModal = () => {
    if (!submitting) {
      resetAddModal();
    }
  };

  const openAddModal = async () => {
    setWarehouseId("");
    setOperatorId("");
    setProducts([]);
    setUnlistedProductOptions([]);
    setSelectedUnlistedProductId("");
    setFormError("");
    setShowAdd(true);
    setOptionsLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}/inventory/evaluations/options/`,
        requestConfig()
      );
      const data = response.data || {};

      setWarehouses(
        Array.isArray(data.warehouses) ? data.warehouses : []
      );
      setOperators(
        Array.isArray(data.stock_operators)
          ? data.stock_operators
          : []
      );
    } catch (error) {
      setWarehouses([]);
      setOperators([]);
      setFormError(
        responseMessage(
          error,
          "Evaluation options could not be loaded."
        )
      );
    } finally {
      setOptionsLoading(false);
    }
  };

  const loadWarehouseProducts = async (event) => {
    const selectedWarehouseId = event.target.value;

    setWarehouseId(selectedWarehouseId);
    setProducts([]);
    setUnlistedProductOptions([]);
    setSelectedUnlistedProductId("");
    setFormError("");

    if (!selectedWarehouseId) {
      return;
    }

    setProductsLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}/inventory/evaluations/warehouse-products/`,
        {
          ...requestConfig(),
          params: {
            warehouse_id: selectedWarehouseId,
          },
        }
      );
      const loadedProducts = Array.isArray(
        response.data?.products
      )
        ? response.data.products
        : [];

      setProducts(
        loadedProducts.map((product) => ({
          product_id: product.product_id,
          product_name: product.product_name,
          expected_quantity: product.expected_quantity,
          min_quantity: product.min_quantity || 0,
          actual_quantity: "",
          reason: "",
        }))
      );
      setUnlistedProductOptions(
        Array.isArray(response.data?.unlisted_products)
          ? response.data.unlisted_products
          : []
      );
    } catch (error) {
      setProducts([]);
      setUnlistedProductOptions([]);
      setSelectedUnlistedProductId("");
      setFormError(
        responseMessage(
          error,
          "Warehouse products could not be loaded."
        )
      );
    } finally {
      setProductsLoading(false);
    }
  };

  const updateActualQuantity = (productId, value) => {
    setProducts((current) =>
      current.map((product) => {
        if (product.product_id !== productId) {
          return product;
        }

        const number = actualNumber(value);

        return {
          ...product,
          actual_quantity: value,
          reason:
            number === product.expected_quantity
              ? ""
              : product.reason,
        };
      })
    );
    setFormError("");
  };

  const updateReason = (productId, value) => {
    setProducts((current) =>
      current.map((product) =>
        product.product_id === productId
          ? { ...product, reason: value }
          : product
      )
    );
    setFormError("");
  };

  const availableUnlistedProducts =
    unlistedProductOptions.filter(
      (option) =>
        !products.some(
          (product) => product.product_id === option.product_id
        )
    );

  const addUnlistedProduct = () => {
    const product = unlistedProductOptions.find(
      (item) =>
        String(item.product_id) === selectedUnlistedProductId
    );

    if (!product) {
      setFormError("Select an unlisted product.");
      return;
    }

    setProducts((current) =>
      current.some((item) => item.product_id === product.product_id)
        ? current
        : [
            ...current,
            {
              product_id: product.product_id,
              product_name: product.product_name,
              expected_quantity: 0,
              actual_quantity: "",
              reason: "",
              is_unlisted: true,
            },
          ]
    );
    setSelectedUnlistedProductId("");
    setFormError("");
  };

  const removeUnlistedProduct = (productId) => {
    setProducts((current) =>
      current.filter(
        (product) =>
          !product.is_unlisted || product.product_id !== productId
      )
    );
    setFormError("");
  };

  const submitEvaluation = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!warehouseId) {
      setFormError("Select a warehouse.");
      return;
    }

    if (!operatorId) {
      setFormError("Select a stock operator.");
      return;
    }

    if (!products.length) {
      setFormError(
        "Add at least one product to evaluate."
      );
      return;
    }

    const normalizedProducts = [];

    for (const product of products) {
      const actual = actualNumber(product.actual_quantity);

      if (actual === null) {
        setFormError(
          `Enter a non-negative whole actual quantity for ${product.product_name}.`
        );
        return;
      }

      if (product.is_unlisted && actual === 0) {
        setFormError(
          `Enter a quantity greater than zero for ${product.product_name}.`
        );
        return;
      }

      const reason = product.reason.trim();

      if (actual !== product.expected_quantity && !reason) {
        setFormError(
          `Enter a reason for the ${product.product_name} discrepancy.`
        );
        return;
      }

      normalizedProducts.push({
        product_id: product.product_id,
        expected_quantity: product.expected_quantity,
        actual_quantity: actual,
        reason:
          actual === product.expected_quantity ? "" : reason,
      });
    }

    setSubmitting(true);

    try {
      const response = await axios.post(
        `${API_URL}/inventory/evaluations/`,
        {
          warehouse_id: Number(warehouseId),
          stock_operator_id: Number(operatorId),
          products: normalizedProducts,
        },
        requestConfig()
      );

      resetAddModal();
      setNotice({
        type: "success",
        text:
          response.data?.message ||
          "Inventory evaluation saved successfully.",
      });
      await fetchEvaluations();
    } catch (error) {
      setFormError(
        responseMessage(
          error,
          "The inventory evaluation could not be saved."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const closeReport = () => {
    setShowReport(false);
    setReport(null);
    setReportError("");
    setReportLoading(false);
  };

  const openReport = useCallback(async (evaluationId) => {
    setShowReport(true);
    setReport(null);
    setReportError("");
    setReportLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}/inventory/evaluations/${evaluationId}/`,
        requestConfig()
      );

      setReport(response.data || null);
    } catch (error) {
      setReportError(
        responseMessage(
          error,
          "The evaluation report could not be loaded."
        )
      );
    } finally {
      setReportLoading(false);
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "warehouse_name",
        headerName: inventoryText("inventory.Warehouse"),
        flex: 2,
        minWidth: 180,
      },
      {
        field: "stock_operator_name",
        headerName: inventoryText("inventory.StockOperator"),
        flex: 2,
        minWidth: 180,
      },
      {
        field: "creation_date",
        headerName: inventoryText("inventory.EvaluationDate"),
        flex: 2,
        minWidth: 190,
        valueFormatter: (params) => formatDate(params.value),
      },
      {
        field: "status_label",
        headerName: inventoryText("inventory.Status"),
        flex: 1,
        minWidth: 130,
        renderCell: (params) => (
          <span
            className={
              "evaluation-status evaluation-status--" +
              (params.row.status === "matched"
                ? "matched"
                : "discrepancy")
            }
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "actions",
        headerName: inventoryText("inventory.Actions"),
        sortable: false,
        filterable: false,
        minWidth: 120,
        renderCell: (params) => (
          <button
            type="button"
            className="edit-client"
            onClick={() => openReport(params.row.evaluation_id)}
          >{inventoryText("inventory.Report")}</button>
        ),
      },
    ],
    [openReport]
  );

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return evaluations.filter((evaluation) => {
      const searchMatch =
        !query ||
        [
          evaluation.warehouse_name,
          evaluation.stock_operator_name,
          evaluation.status_label,
          formatDate(evaluation.creation_date),
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query)
        );

      const filterMatch =
        EVALUATION_FILTER_FIELDS.every(({ field }) => {
          const filterValue = String(
            filters[field] || ""
          )
            .trim()
            .toLowerCase();

          if (!filterValue) {
            return true;
          }

          const rowValue =
            field === "creation_date"
              ? formatDate(evaluation.creation_date)
              : evaluation[field];

          return String(rowValue || "")
            .toLowerCase()
            .includes(filterValue);
        });

      return searchMatch && filterMatch;
    });
  }, [evaluations, filters, searchQuery]);

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleExportToExcel = () => {
    const exportRows = filteredRows.map((evaluation) => {
      const exportRow = {};

      EVALUATION_EXPORT_COLUMNS.forEach(
        ({ field, header }) => {
          if (columnVisibilityModel[field] === false) {
            return;
          }

          exportRow[header] =
            field === "creation_date"
              ? formatDate(evaluation.creation_date)
              : evaluation[field] ?? "";
        }
      );

      return exportRow;
    });

    const worksheet =
      XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Evaluations"
    );

    XLSX.writeFile(
      workbook,
      "Inventory_Evaluations.xlsx"
    );
  };

  const EvaluationToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />

      <Button
        type="button"
        variant="outline-primary"
        onClick={() => setShowFilterModal(true)}
      >
        <img
          src={FilterSVG}
          alt=""
          aria-hidden="true"
        />{inventoryText("inventory.Filter")}</Button>

      <GridToolbarExport />

      <Button
        type="button"
        variant="outline-success"
        onClick={handleExportToExcel}
      >
        <img
          src={ExcelSVG}
          alt=""
          aria-hidden="true"
        />
        Excel
      </Button>
    </GridToolbarContainer>
  );

  return (
    <div className="purchase-page evaluation-page">
      <div className="evaluation-page-actions">
        <button
          type="button"
          className="AddClient"
          onClick={openAddModal}
        >{inventoryText("inventory.AddEvaluation")}</button>
        <NavLink
          exact
          to="/inventory"
          className="AddClient"
          style={{ textDecoration: "none" }}
        >{inventoryText("inventory.GoBack")}</NavLink>
      </div>

      {notice && (
        <div
          className={`evaluation-notice evaluation-notice--${notice.type}`}
          role="status"
        >
          {notice.text}
        </div>
      )}

      <div className="purchase-panel evaluation-panel">
        <div className="evaluation-panel-heading">
          <h2>{inventoryText("inventory.InventoryEvaluations")}</h2>
          <input
            type="search"
            className="evaluation-search"
            placeholder={inventoryText("inventory.Search")}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>

        {pageError && (
          <div className="purchase-form-error" role="alert">
            {pageError}
          </div>
        )}

        <div className="evaluation-grid supplier-table">
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.evaluation_id}
            loading={loading}
            columnVisibilityModel={
              columnVisibilityModel
            }
            onColumnVisibilityModelChange={(model) =>
              setColumnVisibilityModel(model)
            }
            components={{ Toolbar: EvaluationToolbar }}
            rowsPerPageOptions={[10, 100, 1000]}
            pagination
            disableSelectionOnClick
          />
        </div>
      </div>

      <Modal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        className={
          "purchase-modal evaluation-modal " +
          "evaluation-filter-modal"
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{inventoryText("inventory.Filters")}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {EVALUATION_FILTER_FIELDS.map(
            ({ field, label }) => (
              <div
                key={field}
                className="evaluation-filter-row"
              >
                <label
                  htmlFor={`evaluation-filter-${field}`}
                >
                  {label}
                </label>

                {field === "status_label" ? (
                  <select
                    id={`evaluation-filter-${field}`}
                    className={
                      "evaluation-control " +
                      "evaluation-filter-input"
                    }
                    value={filters[field]}
                    onChange={(event) =>
                      handleFilterChange(
                        field,
                        event.target.value
                      )
                    }
                  >
                    <option value="">{inventoryText("inventory.AllStatuses")}</option>
                    <option value="Matched">{inventoryText("inventory.Matched")}</option>
                    <option value="Discrepancy">{inventoryText("inventory.Discrepancy")}</option>
                  </select>
                ) : (
                  <input
                    id={`evaluation-filter-${field}`}
                    type="text"
                    className={
                      "evaluation-control " +
                      "evaluation-filter-input"
                    }
                    placeholder={`Filter by ${label}`}
                    value={filters[field]}
                    autoComplete="off"
                    onChange={(event) =>
                      handleFilterChange(
                        field,
                        event.target.value
                      )
                    }
                  />
                )}
              </div>
            )
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className={
              "purchase-button " +
              "purchase-button-secondary"
            }
            onClick={() => setShowFilterModal(false)}
          >{inventoryText("inventory.Close")}</button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showAdd}
        onHide={closeAddModal}
        className="purchase-modal evaluation-modal"
        size="xl"
        backdrop={submitting ? "static" : true}
      >
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>{inventoryText("inventory.AddEvaluation")}</Modal.Title>
        </Modal.Header>
        <form onSubmit={submitEvaluation}>
          <Modal.Body>

            {formError && (
              <div className="purchase-form-error" role="alert">
                {formError}
              </div>
            )}

            <div className="evaluation-form-options">
              <label>{inventoryText("inventory.Warehouse")}<select
                  className="evaluation-control"
                  value={warehouseId}
                  onChange={loadWarehouseProducts}
                  disabled={optionsLoading || submitting}
                  required
                >
                  <option value="">
                    {optionsLoading
                      ? inventoryText("inventory.Loading")
                      : inventoryText("inventory.SelectWarehouse")}
                  </option>
                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.warehouse_id}
                      value={warehouse.warehouse_id}
                    >
                      {warehouse.warehouse_name}
                    </option>
                  ))}
                </select>
              </label>

              <label>{inventoryText("inventory.StockOperator")}<select
                  className="evaluation-control"
                  value={operatorId}
                  onChange={(event) => {
                    setOperatorId(event.target.value);
                    setFormError("");
                  }}
                  disabled={optionsLoading || submitting}
                  required
                >
                  <option value="">
                    {optionsLoading
                      ? inventoryText("inventory.Loading")
                      : inventoryText("inventory.SelectStockOperator")}
                  </option>
                  {operators.map((operator) => (
                    <option
                      key={operator.employee_id}
                      value={operator.employee_id}
                    >
                      {operator.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {productsLoading && (
              <div className="evaluation-state">{inventoryText("inventory.LoadingWarehouseProducts")}</div>
            )}

            {!productsLoading && warehouseId && !products.length && (
              <div className="evaluation-state">{inventoryText("inventory.ThisWarehouseHasNoListedProducts")}</div>
            )}

            {!productsLoading && products.length > 0 && (
              <div className="evaluation-table-wrap">
                <table className="evaluation-table">
                  <thead>
                    <tr>
                      <th>{inventoryText("inventory.Product")}</th>
                      <th>{inventoryText("inventory.ExpectedQuantity")}</th>
                      <th>{inventoryText("inventory.MinQuantity")}</th>
                      <th>{inventoryText("inventory.ActualQuantity")}</th>
                      <th>{inventoryText("inventory.Difference")}</th>
                      <th>{inventoryText("inventory.Reason")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => {
                      const actual = actualNumber(
                        product.actual_quantity
                      );
                      const different =
                        actual !== null &&
                        actual !== product.expected_quantity;
                      const difference =
                        actual === null
                          ? null
                          : actual - product.expected_quantity;

                      return (
                        <tr key={product.product_id}>
                          <td>
                            {product.product_name}
                            {product.is_unlisted && (
                              <button
                                type="button"
                                className="evaluation-unlisted-remove"
                                onClick={() =>
                                  removeUnlistedProduct(product.product_id)
                                }
                                disabled={submitting}
                              >{inventoryText("inventory.Remove")}</button>
                            )}
                          </td>
                          <td>{product.expected_quantity}</td>
                          <td>{product.min_quantity || 0}</td>
                          <td>
                            <input
                              type="number"
                              min={product.is_unlisted ? "1" : "0"}
                              max={MAX_QUANTITY}
                              step="1"
                              className="evaluation-control evaluation-quantity"
                              value={product.actual_quantity}
                              onChange={(event) =>
                                updateActualQuantity(
                                  product.product_id,
                                  event.target.value
                                )
                              }
                              disabled={submitting}
                              required
                            />
                          </td>
                          <td>
                            {difference === null
                              ? "-"
                              : difference > 0
                              ? `+${difference}`
                              : difference}
                          </td>
                          <td>
                            {different ? (
                              <textarea
                                className="evaluation-control evaluation-reason"
                                value={product.reason}
                                onChange={(event) =>
                                  updateReason(
                                    product.product_id,
                                    event.target.value
                                  )
                                }
                                maxLength="2000"
                                disabled={submitting}
                                required
                              />
                            ) : (
                              <span className="evaluation-muted">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!productsLoading && warehouseId && (
              <div className="evaluation-unlisted-controls">
                <label>{inventoryText("inventory.OptionalProductFoundButNotListed")}<select
                    className="evaluation-control"
                    value={selectedUnlistedProductId}
                    onChange={(event) => {
                      setSelectedUnlistedProductId(event.target.value);
                      setFormError("");
                    }}
                    disabled={
                      submitting || !availableUnlistedProducts.length
                    }
                  >
                    <option value="">
                      {availableUnlistedProducts.length
                        ? "Select Product"
                        : "No Additional Products Available"}
                    </option>
                    {availableUnlistedProducts.map((product) => (
                      <option
                        key={product.product_id}
                        value={product.product_id}
                      >
                        {product.product_name}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="purchase-button purchase-button-secondary"
                  onClick={addUnlistedProduct}
                  disabled={
                    submitting || !selectedUnlistedProductId
                  }
                >{inventoryText("inventory.AddUnlistedProduct")}</button>
              </div>
            )}

          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="purchase-button purchase-button-secondary"
              onClick={closeAddModal}
              disabled={submitting}
            >{inventoryText("inventory.Cancel")}</button>
            <button
              type="submit"
              className="purchase-button purchase-button-primary"
              disabled={
                submitting ||
                optionsLoading ||
                productsLoading ||
                !products.length
              }
            >
              {submitting
                ? inventoryText("inventory.Saving")
                : inventoryText("inventory.Confirm")}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={showReport}
        onHide={closeReport}
        className="purchase-modal evaluation-modal"
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>{inventoryText("inventory.EvaluationReport")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {reportLoading && (
            <div className="evaluation-state">{inventoryText("inventory.LoadingEvaluationReport")}</div>
          )}

          {reportError && (
            <div className="purchase-form-error" role="alert">
              {reportError}
            </div>
          )}

          {!reportLoading && report && (
            <>
              <dl className="evaluation-report-summary">
                <div>
                  <dt>{inventoryText("inventory.Warehouse")}</dt>
                  <dd>{report.warehouse_name}</dd>
                </div>
                <div>
                  <dt>{inventoryText("inventory.StockOperator")}</dt>
                  <dd>{report.stock_operator_name}</dd>
                </div>
                <div>
                  <dt>{inventoryText("inventory.EvaluationDate")}</dt>
                  <dd>{formatDate(report.creation_date)}</dd>
                </div>
                <div>
                  <dt>{inventoryText("inventory.Status")}</dt>
                  <dd>
                    <span
                      className={
                        "evaluation-status evaluation-status--" +
                        (report.status === "matched"
                          ? "matched"
                          : "discrepancy")
                      }
                    >
                      {report.status_label}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>{inventoryText("inventory.Products")}</dt>
                  <dd>{report.product_count}</dd>
                </div>
                <div>
                  <dt>{inventoryText("inventory.Discrepancies")}</dt>
                  <dd>{report.discrepancy_products}</dd>
                </div>
              </dl>

              <div className="evaluation-table-wrap">
                <table className="evaluation-table evaluation-report-table">
                  <thead>
                    <tr>
                      <th>{inventoryText("inventory.Product")}</th>
                      <th>{inventoryText("inventory.ExpectedQuantity")}</th>
                      <th>{inventoryText("inventory.MinQuantity")}</th>
                      <th>{inventoryText("inventory.ActualQuantity")}</th>
                      <th>{inventoryText("inventory.Difference")}</th>
                      <th>{inventoryText("inventory.Reason")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.products || []).map((product) => (
                      <tr key={product.product_id}>
                        <td>{product.product_name}</td>
                        <td>{product.expected_quantity}</td>
                        <td>
                          {product.min_quantity === undefined
                            || product.min_quantity === null
                            ? "—"
                            : product.min_quantity}
                        </td>
                        <td>{product.actual_quantity}</td>
                        <td>
                          {product.difference > 0
                            ? `+${product.difference}`
                            : product.difference}
                        </td>
                        <td>{product.reason || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="purchase-button purchase-button-secondary"
            onClick={closeReport}
          >{inventoryText("inventory.Close")}</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default InventoryEvaluations;
